import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { Plus, LogOut, Pencil, Trash2, Eye, EyeOff, Inbox, CalendarClock, BarChart3, LayoutGrid } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [tab, setTab] = useState("posts");

  const loadAll = async () => {
    try {
      const [pRes, aRes, mRes] = await Promise.all([
        api.get("/blog/all"),
        api.get("/appointments"),
        api.get("/contact"),
      ]);
      setPosts(pRes.data || []);
      setAppointments(aRes.data || []);
      setMessages(mRes.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Beitrag wirklich löschen?")) return;
    try {
      await api.delete(`/blog/${id}`);
      setPosts((p) => p.filter((x) => x.id !== id));
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  const togglePublish = async (post) => {
    try {
      await api.put(`/blog/${post.id}`, { published: !post.published });
      setPosts((arr) =>
        arr.map((p) => (p.id === post.id ? { ...p, published: !post.published } : p))
      );
    } catch (e) {
      alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-dashboard" className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-20 pb-24">
      <Helmet>
        <title>Admin Dashboard | Karim Benna IT Consulting</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--text-2)] mb-3">
            Admin · {user?.email}
          </div>
          <h1 className="font-display text-4xl lg:text-5xl tracking-tight">
            Dashboard
          </h1>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Link to="/admin/content" data-testid="content-nav-btn" className="btn-ghost text-xs">
            <LayoutGrid size={14} /> Inhalte verwalten
          </Link>
          <Link to="/admin/stats" data-testid="stats-nav-btn" className="btn-ghost text-xs">
            <BarChart3 size={14} /> Statistiken
          </Link>
          <Link to="/admin/posts/new" data-testid="new-post-btn" className="btn-primary text-xs">
            <Plus size={14} /> Neuer Beitrag
          </Link>
          <button
            onClick={async () => {
              await logout();
              navigate("/admin/login");
            }}
            data-testid="logout-btn"
            className="btn-ghost text-xs"
          >
            <LogOut size={14} /> Abmelden
          </button>
        </div>
      </div>

      <div className="border-b hairline flex gap-8 mb-8">
        {[
          { k: "posts", l: "Beiträge", n: posts.length },
          { k: "appointments", l: "Termin-Anfragen", n: appointments.length },
          { k: "messages", l: "Nachrichten", n: messages.length },
        ].map((t) => (
          <button
            key={t.k}
            data-testid={`tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`py-3 -mb-px border-b-2 transition-colors font-mono text-xs uppercase tracking-[0.2em] ${
              tab === t.k ? "border-[var(--gold)] text-[var(--gold)]" : "border-transparent text-[var(--text-2)] hover:text-[var(--text-0)]"
            }`}
          >
            {t.l} <span className="opacity-60">({t.n})</span>
          </button>
        ))}
      </div>

      {tab === "posts" && (
        <div data-testid="posts-tab" className="border hairline">
          {posts.length === 0 ? (
            <div className="p-12 text-center text-[var(--text-2)]">Noch keine Beiträge.</div>
          ) : (
            posts.map((p) => (
              <div
                key={p.id}
                className="grid grid-cols-1 md:grid-cols-12 gap-4 px-6 py-5 border-b hairline last:border-b-0 items-center"
              >
                <div className="md:col-span-6">
                  <div className="text-[var(--text-0)] font-display text-lg">{p.title}</div>
                  <div className="text-[var(--text-2)] text-xs font-mono mt-1">/{p.slug}</div>
                </div>
                <div className="md:col-span-2 font-mono text-xs text-[var(--text-2)]">
                  {new Date(p.created_at).toLocaleDateString("de-DE")}
                </div>
                <div className="md:col-span-2">
                  <span
                    className={`tag-pill ${
                      p.published ? "border-[var(--gold)] text-[var(--gold)]" : ""
                    }`}
                  >
                    {p.published ? "Veröffentlicht" : "Entwurf"}
                  </span>
                </div>
                <div className="md:col-span-2 flex gap-2 justify-end">
                  <button
                    onClick={() => togglePublish(p)}
                    data-testid={`toggle-${p.id}`}
                    className="w-9 h-9 flex items-center justify-center border hairline hover:border-[var(--gold)] hover:text-[var(--gold)]"
                    title={p.published ? "Verbergen" : "Veröffentlichen"}
                  >
                    {p.published ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <Link
                    to={`/admin/posts/${p.id}`}
                    data-testid={`edit-${p.id}`}
                    className="w-9 h-9 flex items-center justify-center border hairline hover:border-[var(--gold)] hover:text-[var(--gold)]"
                  >
                    <Pencil size={14} />
                  </Link>
                  <button
                    onClick={() => onDelete(p.id)}
                    data-testid={`delete-${p.id}`}
                    className="w-9 h-9 flex items-center justify-center border hairline hover:border-red-500 hover:text-red-500"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "appointments" && (
        <div data-testid="appointments-tab" className="border hairline">
          {appointments.length === 0 ? (
            <div className="p-12 text-center text-[var(--text-2)]">Keine Anfragen.</div>
          ) : (
            appointments.map((a) => (
              <div key={a.id} className="px-6 py-5 border-b hairline last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-1">
                  <CalendarClock size={18} className="text-[var(--gold)]" />
                </div>
                <div className="md:col-span-3">
                  <div className="text-[var(--text-0)]">{a.name}</div>
                  <div className="text-[var(--text-2)] text-xs">{a.email}</div>
                  {a.company && <div className="text-[var(--text-2)] text-xs">{a.company}</div>}
                </div>
                <div className="md:col-span-3 font-mono text-xs text-[var(--gold)]">
                  {a.preferred_date} · {a.preferred_time}
                </div>
                <div className="md:col-span-5">
                  <div className="text-[var(--text-0)] text-sm">{a.topic}</div>
                  {a.message && <div className="text-[var(--text-2)] text-xs mt-1">{a.message}</div>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "messages" && (
        <div data-testid="messages-tab" className="border hairline">
          {messages.length === 0 ? (
            <div className="p-12 text-center text-[var(--text-2)]">Keine Nachrichten.</div>
          ) : (
            messages.map((m) => (
              <div key={m.id} className="px-6 py-5 border-b hairline last:border-b-0 grid grid-cols-1 md:grid-cols-12 gap-3">
                <div className="md:col-span-1">
                  <Inbox size={18} className="text-[var(--gold)]" />
                </div>
                <div className="md:col-span-3">
                  <div className="text-[var(--text-0)]">{m.name}</div>
                  <div className="text-[var(--text-2)] text-xs">{m.email}</div>
                </div>
                <div className="md:col-span-3 text-[var(--text-0)] text-sm">{m.subject}</div>
                <div className="md:col-span-5 text-[var(--text-1)] text-sm">{m.message}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
